import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { distinctUntilChanged, filter, skip, takeUntil } from 'rxjs/operators';

import { Userpilot } from 'userpilot';

import { IframeService, LoadingSpinnerService } from '@common';
import { BaseComponentOnDestroy } from '@common/epics/base-component-on-destroy';
import { AppVersionService } from '@common/services/app-version.service';
import { AuthService } from '@common/services/auth.service';
import { BroadcastChannelService } from '@common/services/broadcast-channel.service';
import { ConnectivityService } from '@common/services/connectivity.service';
import { GoogleAnalyticsService } from '@common/services/google-analytics/google-analytics.service';
import { ToastService } from '@common/services/toast.service';
import { endInAppNavigation, startInAppNavigation } from '@common/utility/is-navigating-in-app.util';
import { environment } from '@environment';


import { LoadAnalyticsScripts } from './analyticsLoader';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.less'],
  encapsulation: ViewEncapsulation.None,
  host: {
  // Adding a class compensating for the offset appearing for the root component in dev mode.
  // This binding is actually the conventional way, so the linter needs to be silenced.
  // See https://angular.dev/guide/components/host-elements#binding-to-the-host-element
  // eslint-disable-next-line @typescript-eslint/naming-convention 
  '[class.development]': `${(!environment.production)}`
  },
  standalone: false
})
export class AppComponent extends BaseComponentOnDestroy implements OnInit {
  private readonly router = inject(Router);
  private readonly loadingSpinnerService = inject(LoadingSpinnerService);
  private readonly iframeService = inject(IframeService);
  private readonly authService = inject(AuthService);
  private readonly broadcastChannelService = inject(BroadcastChannelService);
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly appVersionService = inject(AppVersionService);
  private readonly connectivityService = inject(ConnectivityService);
  private readonly toastService = inject(ToastService);

  isIframe = false;

  constructor() {
    super();
    //  This is to avoid reload during acquireTokenSilent() because of hidden iframe
    this.isIframe = this.iframeService.isIframe;
  }

  ngOnInit() {
    this.loadAnalytics();
    this.setupBroadcastChannelListener();
    this.appVersionService.start();
    this.setupConnectivityMonitoring();

    this.router.events.pipe(filter(event => event instanceof NavigationStart &&  event.url.startsWith('/#')),
        takeUntil(this.isDestroyed$))
        .subscribe((event: NavigationStart) => this.router.navigateByUrl(event.url.replace('/#', '')));

    this.router.events.pipe(takeUntil(this.isDestroyed$)).subscribe(e => {
      if (e instanceof NavigationStart) {
        startInAppNavigation();
        this.loadingSpinnerService.show();
      }
      if (e instanceof NavigationCancel) {
        endInAppNavigation();
        this.loadingSpinnerService.hide();
      }
      if (e instanceof NavigationEnd) {
        endInAppNavigation();
        this.loadingSpinnerService.hide();

        if (this.authService.isLoggedIn()) {
          this.authService.getUserContext().then(() => {
            // reload user pilot only when user had been authenticated.
            Userpilot.reload();

            const pageViewEventData = {
              pageDom: window.location.hostname,
              pageTitle: document.title
            };

            this.googleAnalyticsService.sendPageViewData(pageViewEventData);
          });
        }
      }
      if (e instanceof NavigationError) {
        endInAppNavigation();
        this.loadingSpinnerService.hideAll();
      }
    });
  }

  private loadAnalytics(): void {
    if (!this.isIframe) {
      const googleAnalyticsDataLayer = LoadAnalyticsScripts();
      this.googleAnalyticsService.init(googleAnalyticsDataLayer);
    }
  }

  private setupConnectivityMonitoring(): void {
    this.connectivityService.start();

    // skip(1): don't toast the initial value on load, the header icon already reflects it.
    this.connectivityService.isOnline$.pipe(
      distinctUntilChanged(),
      skip(1),
      takeUntil(this.isDestroyed$)
    ).subscribe(isOnline => {
      if (isOnline) {
        this.toastService.logSuccess($localize`:@@app.connectivity.restoredToast:Connection restored.`);
      } else {
        this.toastService.logWarning($localize`:@@app.connectivity.offlineToast:You appear to be offline. Some actions may fail until your connection is restored.`, true);
      }
    });
  }

  private setupBroadcastChannelListener(): void {
    this.broadcastChannelService.getMessageStream()
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe(message => {
        if (message.action === this.broadcastChannelService.actions.channelTabsRefresh) {
          window.location.reload();
        }
      });
  }
}
